/**
 * Policy configuration — the single source of truth for every threshold in §33.
 *
 * Previously these numbers lived in two places (a YAML file for the agent and a `types.ts`
 * constant for the UI, with a comment asking future readers to keep them in sync). They
 * drift the moment anyone touches one of them, and a UI showing different thresholds from
 * the ones that produced an event is worse than a UI showing none. So there is now exactly
 * one definition, in one package, consumed by agent, backend and dashboard alike.
 *
 * Two properties matter more than convenience:
 *
 * 1. **Unknown keys are an error.** A misspelled threshold in a policy file must not
 *    silently leave the safe default in place while an administrator believes they changed
 *    it. Known keys are derived from `DEFAULT_POLICY` itself, so there is no second schema
 *    to keep aligned.
 * 2. **Cross-field validation.** Individually valid numbers can form an incoherent policy
 *    (an "away" band shorter than the "ignore" band, a forward cone wider than the
 *    strongly-away cone). `validatePolicy` rejects those combinations.
 *
 * Defaults here are conservative engineering defaults chosen to *avoid false accusations*.
 * They are not organisational policy; an organisation overrides them.
 */

import { ConfigError } from './errors.js';

// ---------------------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------------------

export interface CameraPolicy {
  deviceId: string;
  width: number;
  height: number;
  captureFps: number;
  warmupFrames: number;
  reopenBackoffSeconds: number;
  maxReopenAttempts: number;
  streamIsMirrored: boolean;
}

export interface VisionPolicy {
  provider: string;
  modelAssetPath: string;
  wasmBasePath: string;
  delegate: 'auto' | 'cpu' | 'gpu';
  maxFaces: number;
  minDetectionConfidence: number;
  minLandmarkConfidence: number;
  minPoseConfidence: number;
  presenceConfidence: number;
  maxReprojectionErrorPx: number;
  /**
   * Emit face blendshapes. Required for the smile liveness challenge — without it that
   * challenge reports "unavailable" and fails rather than passing unmeasured.
   */
  outputBlendshapes: boolean;
  inferenceWidth: number;
  targetProcessFps: number;
  minProcessFps: number;
  maxProcessFps: number;
  adaptiveFps: boolean;
  frameBudgetMs: number;
}

export interface ThresholdPolicy {
  yawForwardMax: number;
  yawStrongAway: number;
  pitchUpMax: number;
  pitchDownMax: number;
  pitchStrongAway: number;
  rollTolerance: number;
  hysteresisDegrees: number;
}

export interface SmoothingPolicy {
  windowSeconds: number;
  minSamples: number;
  maxSampleAgeSeconds: number;
  stabilityReferenceDegrees: number;
}

export interface QualityPolicy {
  minBrightness: number;
  maxBrightness: number;
  minBlurVariance: number;
  minFaceBoxRatio: number;
}

export interface EventPolicy {
  ignoreBelowSeconds: number;
  attentionAwayMinSeconds: number;
  potentiallySignificantSeconds: number;
  faceLostAwaySeconds: number;
  idleTimeoutSeconds: number;
  minStateDwellSeconds: number;
  emitMinorShifts: boolean;
  maxEventsPerMinute: number;
}

export interface CalibrationPolicy {
  enabled: boolean;
  samplesPerStep: number;
  secondsPerStep: number;
  settleSeconds: number;
  thresholdFraction: number;
  minYawThreshold: number;
  maxYawThreshold: number;
  minPitchThreshold: number;
  maxPitchThreshold: number;
  maxProfileAgeDays: number;
}

export interface EnrolmentPolicy {
  /** Whether an un-enrolled operator is blocked from the console until enrolment completes. */
  required: boolean;
  /** How many challenges to draw. More is stronger but slower and more annoying. */
  challengeCount: number;
  /** Continuous seconds a pose or expression must be held before it counts. */
  holdSeconds: number;
  challengeTimeoutSeconds: number;
  sessionTimeoutSeconds: number;
  /**
   * Require a return to neutral between challenges.
   *
   * This is the cheapest meaningful anti-replay measure: a still image cannot move between
   * poses, and a pre-recorded video has to match a challenge order it could not have known.
   */
  requireNeutralBetween: boolean;
  /** Degrees from neutral, on top of ordinary monitoring thresholds, to satisfy a turn. */
  minChallengeYawDegrees: number;
  minChallengePitchDegrees: number;
  /** Blendshape smile score (0..1) required for the smile challenge. */
  smileThreshold: number;
  /** Frames below this pose confidence cannot satisfy a challenge. */
  minFrameConfidence: number;
  /**
   * How many near-frontal frames to capture for the face template, after liveness passes.
   *
   * The template is built from these and **not** from the challenge frames. The challenges
   * deliberately push the head to extremes; averaging a left profile with a right profile
   * yields a centroid that resembles neither, and every later frontal comparison then scores
   * as a mismatch. Liveness proves a live person; these frames describe their face.
   */
  templateFrameCount: number;
  /** Minimum gap between template frames, so they are not near-duplicates of one instant. */
  templateFrameIntervalSeconds: number;
  /**
   * Yaw/pitch envelope, in degrees from neutral, that counts as "frontal".
   *
   * Used for both template capture and live verification. The embedding service crops by
   * bounding box without landmark alignment, which makes its embeddings markedly
   * pose-sensitive, so comparisons are only meaningful between similarly-framed faces.
   * Outside this envelope the honest answer is "not verified", never "not you".
   */
  frontalYawMaxDegrees: number;
  frontalPitchMaxDegrees: number;
}

export interface SessionGuardPolicy {
  enabled: boolean;
  /**
   * `log_only` records every would-be lock without acting on it.
   *
   * Start here. It is the only way to measure the false-positive rate against a real office
   * before a misfire can cost someone an in-progress loan review.
   */
  mode: 'log_only' | 'enforce';
  /** Continuous absence of a detected face before the session locks. */
  faceAbsentLockSeconds: number;
  /** Continuous identity mismatch before the session locks. */
  identityMismatchLockSeconds: number;
  /**
   * Continuous camera unavailability — while the host surface *is* visible — before the session
   * locks.
   *
   * This exists because "the camera stopped working" and "the window is in the background" are
   * not the same situation, though both leave the guard blind. A backgrounded window is the
   * employee legitimately using another application, and must never lock. A dead camera in front
   * of a visible window is either a hardware fault or somebody disabling the sensor to escape
   * monitoring, and treating it as benign makes the whole control opt-out: switch off the webcam
   * and no absence is ever recorded.
   *
   * Longer than `faceAbsentLockSeconds` on purpose — a camera can drop a stream briefly when
   * another application grabs it, or when a laptop wakes from sleep, and that should recover
   * rather than lock.
   */
  cameraUnavailableLockSeconds: number;
  /**
   * Continuous time with the console in the background before the session locks.
   *
   * Switching tab or application takes the console out of view, and a browser cannot reliably
   * observe a hidden tab — the video element may stall, so absence there is undetectable rather
   * than merely unobserved. Rather than leave that as a blind spot, backgrounding is itself a
   * trigger: an authenticated console nobody is looking at is the unattended-session risk this
   * feature exists for.
   *
   * Set to 0 to disable, which restores the previous behaviour of ignoring backgrounding.
   *
   * The number needs care. Too short and it fires whenever somebody checks a reference in
   * another tab, which is constant in real work and gets the whole control switched off. Too
   * long and the blind spot returns. Tune it per environment with
   * RUNTIME_FOCUS_AGENT__SESSION_GUARD__SURFACE_HIDDEN_LOCK_SECONDS.
   */
  surfaceHiddenLockSeconds: number;
  /** Cosine similarity at or above which an embedding counts as the enrolled employee. */
  minMatchSimilarity: number;
  /** How often to re-verify identity against the enrolled template. */
  identityCheckIntervalSeconds: number;
  /**
   * An identity result older than this is treated as unverified rather than as a match.
   * A stale verdict must not keep a session open indefinitely, nor lock it — see the guard.
   */
  identityResultMaxAgeSeconds: number;
  /** Quiet period after a successful unlock, so the guard cannot immediately re-lock. */
  graceSecondsAfterUnlock: number;
  reauthMethod: 'face' | 'password' | 'either';
}

export interface PrivacyPolicy {
  policyVersion: string;
  /**
   * Whether to show a one-time acknowledgement before the camera is opened.
   *
   * Set false by operator decision (2026-08-25): the admin portal opens the camera on the
   * browser's own permission prompt, with no in-app notice. The consent machinery in
   * `privacy/consent.ts` is retained and tested, so this is a one-flag change if the
   * disclosure requirement is later brought in-app rather than handled by HR policy.
   */
  requireAcknowledgement: boolean;
  storeRawFrames: boolean;
  retentionDays: number;
  requireVisibleIndicator: boolean;
  indicatorProvidedByHost: boolean;
  collectSignals: string[];
  uploadBatchSeconds: number;
}

export interface PresentationPolicy {
  showLandmarks: boolean;
  showPoseAxes: boolean;
  showDebugPanel: boolean;
  mirrorDisplay: boolean;
}

export interface RuntimePolicy {
  logLevel: 'debug' | 'info' | 'warn' | 'error';
  heartbeatSeconds: number;
  sessionId: string;
  maxRunSeconds: number;
}

export interface Policy {
  camera: CameraPolicy;
  vision: VisionPolicy;
  thresholds: ThresholdPolicy;
  smoothing: SmoothingPolicy;
  quality: QualityPolicy;
  events: EventPolicy;
  calibration: CalibrationPolicy;
  privacy: PrivacyPolicy;
  enrolment: EnrolmentPolicy;
  sessionGuard: SessionGuardPolicy;
  presentation: PresentationPolicy;
  runtime: RuntimePolicy;
}

// ---------------------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------------------

export const DEFAULT_POLICY: Policy = {
  camera: {
    deviceId: '',
    width: 640,
    height: 480,
    captureFps: 30,
    /** Frames discarded after opening — webcam auto-exposure needs a moment to settle. */
    warmupFrames: 5,
    reopenBackoffSeconds: 2,
    /** 0 = retry forever. §27 requires surviving an unplug for a whole workday. */
    maxReopenAttempts: 0,
    /**
     * Whether the capture stream is horizontally flipped relative to reality.
     *
     * Device-dependent and not detectable from a single frame: most webcams deliver the true
     * sensor view, but some drivers and virtual cameras pre-mirror it. Getting it wrong swaps
     * left and right in every direction statistic and every multi-monitor mapping — the
     * numbers stay plausible, which is what makes it dangerous.
     *
     * When true, the host flips landmark x-coordinates before the core sees them, so the
     * whole pipeline operates on a canonical unmirrored frame with no special cases
     * downstream. This is independent of `presentation.mirrorDisplay`, which only affects
     * what the employee sees.
     *
     * To determine it: turn your head to your own left and check the reported direction. If it
     * says "right", set this to true. A completed calibration also detects it — the session
     * warns loudly when a step moved the opposite way to its prompt.
     */
    streamIsMirrored: false,
  },
  vision: {
    provider: 'mediapipe',
    /**
     * Local paths, not URLs. The previous implementation fetched both the WASM runtime and
     * the model from public CDNs on first camera use. A monitoring agent that reaches out
     * to third-party hosts at runtime cannot work offline, cannot be deployed air-gapped,
     * and hands a supply-chain dependency to whoever controls that host. Assets are
     * vendored and served from our own origin; `npm run fetch:models` populates them once.
     */
    modelAssetPath: '/models/face_landmarker.task',
    wasmBasePath: '/wasm',
    /** `auto` tries GPU then falls back to CPU; machines without WebGL must still work. */
    delegate: 'auto',
    /**
     * Two, not one. Detecting a second face is what makes "more than one person in frame"
     * observable, which is a precondition for refusing to attribute a bystander's head
     * movements to the employee.
     */
    maxFaces: 2,
    /**
     * Deliberately permissive (0.3, not the usual 0.5).
     *
     * A strict detection threshold fails in the worst direction for this product: at large
     * pitch or yaw the detector's confidence drops and it returns *nothing*, so a person
     * sitting at their desk looking up at the ceiling produces "no face visible" and, after
     * `faceLostAwaySeconds`, "away from workstation". Reporting someone as absent while they
     * are plainly present is far more damaging than carrying a slightly noisier landmark set
     * — and noise is already handled downstream by the median smoother, the stability
     * measure and the pose-confidence floor, none of which can recover a frame that was
     * never detected.
     *
     * Raise these only if a camera environment produces frequent spurious detections.
     */
    minDetectionConfidence: 0.3,
    minLandmarkConfidence: 0.3,
    /** Below this, direction is reported but the state becomes `unknown`. */
    minPoseConfidence: 0.35,
    /**
     * Confidence assigned to "a face is present" when the provider exposes no per-frame
     * detection score. MediaPipe returns landmarks or nothing, so rather than fabricate a
     * probability the agent records a configured presence confidence meaning "the detector
     * fired above its threshold", and lets pose confidence carry the real variation.
     */
    presenceConfidence: 0.9,
    maxReprojectionErrorPx: 12,
    outputBlendshapes: true,
    inferenceWidth: 480,
    targetProcessFps: 10,
    minProcessFps: 3,
    maxProcessFps: 15,
    adaptiveFps: true,
    frameBudgetMs: 45,
  },
  thresholds: {
    yawForwardMax: 30,
    yawStrongAway: 60,
    pitchUpMax: 22,
    /** Asymmetric on purpose: reading a keyboard or notes is a normal downward pose. */
    pitchDownMax: 25,
    pitchStrongAway: 50,
    rollTolerance: 35,
    /**
     * Extra margin required to return to FORWARD, so a pose resting on the boundary does
     * not oscillate and manufacture dozens of spurious events.
     */
    hysteresisDegrees: 6,
  },
  smoothing: {
    windowSeconds: 1.5,
    /** Below this the direction is `not_detected` rather than a single-frame guess (§6). */
    minSamples: 3,
    maxSampleAgeSeconds: 3,
    /**
     * MAD at which head movement counts as fully unstable. A head in motion produces a
     * scattered window; the median of that window is a number but not a trustworthy one.
     */
    stabilityReferenceDegrees: 12,
  },
  quality: {
    minBrightness: 35,
    maxBrightness: 240,
    minBlurVariance: 25,
    minFaceBoxRatio: 0.1,
  },
  events: {
    /**
     * Three edges define the four §11 bands, so they cannot contradict each other:
     *   [0, ignoreBelow)                              ignored entirely
     *   [ignoreBelow, attentionAwayMin)               minor shift (counted, not an event)
     *   [attentionAwayMin, potentiallySignificant)    attention-away event
     *   [potentiallySignificant, inf)                 potentially significant event
     */
    ignoreBelowSeconds: 2,
    attentionAwayMinSeconds: 5,
    potentiallySignificantSeconds: 30,
    /** No face for this long => AWAY (not "distracted" — the person has left). */
    faceLostAwaySeconds: 15,
    /**
     * Three minutes, not thirty seconds: reading, thinking and listening in a meeting all
     * involve minutes without input, and none of them are an absence of work.
     */
    idleTimeoutSeconds: 180,
    minStateDwellSeconds: 2,
    emitMinorShifts: false,
    /** Rate cap. Protects the backend and flags a misconfigured or failing sensor. */
    maxEventsPerMinute: 30,
  },
  calibration: {
    enabled: true,
    samplesPerStep: 20,
    secondsPerStep: 4,
    settleSeconds: 1.5,
    /** Derived threshold sits this far between neutral and the employee's own extreme. */
    thresholdFraction: 0.55,
    minYawThreshold: 12,
    maxYawThreshold: 45,
    minPitchThreshold: 10,
    maxPitchThreshold: 40,
    /** Seating and camera position drift; stale profiles must be re-confirmed. */
    maxProfileAgeDays: 90,
  },
  enrolment: {
    /**
     * False by default so that enabling face enrolment is a deliberate act.
     *
     * Turning this on blocks console access for anyone not yet enrolled — including whoever
     * turns it on. Keep an escape hatch (see docs) before enabling it in an environment you
     * cannot afford to be locked out of.
     */
    required: false,
    challengeCount: 3,
    /** Long enough that a fluke frame mid-sweep cannot satisfy a challenge. */
    holdSeconds: 0.8,
    challengeTimeoutSeconds: 20,
    sessionTimeoutSeconds: 120,
    requireNeutralBetween: true,
    /**
     * Deliberately larger than `thresholds.yawForwardMax` (30): enrolment should require an
     * unambiguous, deliberate turn rather than the smallest movement the monitor would notice.
     */
    minChallengeYawDegrees: 25,
    minChallengePitchDegrees: 18,
    smileThreshold: 0.4,
    minFrameConfidence: 0.4,
    templateFrameCount: 5,
    templateFrameIntervalSeconds: 0.4,
    /**
     * Tight on purpose. Widening these lets off-angle frames into the comparison, which is
     * precisely what produces "same person, different angle, reported as a mismatch".
     */
    frontalYawMaxDegrees: 15,
    frontalPitchMaxDegrees: 12,
  },
  sessionGuard: {
    enabled: false,
    // Ships in log-only so the false-positive rate can be measured before it bites.
    mode: 'log_only',
    faceAbsentLockSeconds: 15,
    /**
     * Longer than absence on purpose. A mismatch is the more serious finding but also the
     * more error-prone one: an unusual angle, harsh backlight or a partly occluded face all
     * depress similarity. Requiring it to persist filters those out.
     */
    identityMismatchLockSeconds: 20,
    cameraUnavailableLockSeconds: 30,
    // Disabled by default: locking on backgrounding is a deliberate policy choice, not something
    // a host should inherit by merging.
    surfaceHiddenLockSeconds: 0,
    /**
     * ArcFace-family cosine similarity. 0.36 is the commonly used operating point for
     * buffalo_l at roughly 1e-4 FAR; it must be tuned against your own enrolled population
     * before enforcement, not taken as a default that happens to work.
     */
    minMatchSimilarity: 0.36,
    identityCheckIntervalSeconds: 180,
    identityResultMaxAgeSeconds: 600,
    graceSecondsAfterUnlock: 30,
    reauthMethod: 'either',
  },
  privacy: {
    policyVersion: '1.0.0',
    requireAcknowledgement: false,
    /** MUST stay false outside an explicitly authorised debugging window (§28). */
    storeRawFrames: false,
    retentionDays: 30,
    requireVisibleIndicator: true,
    indicatorProvidedByHost: false,
    /**
     * Allow-list of signal families the agent may derive. Data minimisation is enforced
     * here rather than by convention: a family absent from this list is not computed.
     */
    collectSignals: ['face_detected', 'head_pose', 'frame_quality', 'attention_events'],
    uploadBatchSeconds: 45,
  },
  presentation: {
    showLandmarks: false,
    showPoseAxes: true,
    showDebugPanel: true,
    /** Mirror the *display* only; pose is always computed on the unmirrored frame. */
    mirrorDisplay: true,
  },
  runtime: {
    logLevel: 'info',
    heartbeatSeconds: 60,
    /** Blank = generate one per session. */
    sessionId: '',
    /** 0 = run until stopped. Non-zero is used by demos, tests and benchmarks. */
    maxRunSeconds: 0,
  },
};

/**
 * Closed vocabulary of collectable signal families.
 *
 * Being a closed set is the point: there is no family here for identity, emotion, age,
 * gender, health or keystroke content, so a policy file cannot ask for one and the pipeline
 * has nowhere to put one (§3, §32).
 */
export const KNOWN_SIGNAL_FAMILIES: ReadonlySet<string> = new Set([
  // Phase 1
  'face_detected',
  'head_pose',
  'frame_quality',
  'attention_events',
  // Phase 2 — declared so a policy can be written ahead of the code; the pipeline only
  // computes families it actually implements.
  'gaze_direction',
  'keyboard_activity',
  'mouse_activity',
  'idle_duration',
  'application_category',
  'meeting_state',
]);

export const ENV_PREFIX = 'FOCUS_AGENT__';
export const RAW_FRAME_ACK_ENV = 'FOCUS_AGENT_ALLOW_RAW_FRAMES';

// ---------------------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------------------

export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? (T[K] extends unknown[] ? T[K] : DeepPartial<T[K]>) : T[K];
};

export interface ResolvedPolicy {
  policy: Policy;
  /** Dotted key -> the layer that set it (`override` or `env`). */
  sources: Record<string, string>;
  /** Short fingerprint stamped onto every event for traceability. */
  version: string;
}

export interface ResolveOptions {
  overrides?: DeepPartial<Policy> | Record<string, unknown>;
  env?: Record<string, string | undefined>;
  validate?: boolean;
}

/**
 * Resolve the effective policy: defaults -> environment -> explicit overrides.
 *
 * Provenance is recorded per leaf. That is not decoration: when a manager disputes a score
 * the first question is "which thresholds were in force and who set them", and this is what
 * answers it.
 */
export function resolvePolicy(options: ResolveOptions = {}): ResolvedPolicy {
  const sources: Record<string, string> = {};
  const merged = structuredCloneCompat(DEFAULT_POLICY) as unknown as Record<string, unknown>;

  if (options.env) {
    applyLayer(merged, envToNested(options.env), sources, 'env', '');
  }
  if (options.overrides) {
    applyLayer(merged, explodeDotted(options.overrides), sources, 'override', '');
  }

  const policy = merged as unknown as Policy;

  if (options.validate !== false) {
    const problems = validatePolicy(policy).filter(
      (problem) => !isAcknowledgedRawFrameProblem(problem, options.env),
    );
    if (problems.length > 0) {
      throw new ConfigError('Invalid work-focus policy', problems);
    }
  }

  return { policy, sources, version: policyVersion(policy) };
}

function applyLayer(
  target: Record<string, unknown>,
  incoming: Record<string, unknown>,
  sources: Record<string, string>,
  label: string,
  prefix: string,
): void {
  for (const [key, value] of Object.entries(incoming)) {
    const dotted = prefix ? `${prefix}.${key}` : key;
    if (!(key in target)) {
      throw new ConfigError(
        `${dotted}: unknown policy key. Known keys at this level: ${Object.keys(target)
          .sort()
          .join(', ')}`,
      );
    }
    const existing = target[key];
    if (isPlainObject(existing)) {
      if (!isPlainObject(value)) {
        throw new ConfigError(`${dotted}: expected an object of settings`);
      }
      applyLayer(existing as Record<string, unknown>, value, sources, label, dotted);
      continue;
    }
    target[key] = coerce(value, existing, dotted);
    sources[dotted] = label;
  }
}

/**
 * Coerce an incoming value to the type of the default it replaces.
 *
 * Deriving the target type from the default is what removes the need for a second schema
 * declaration — and therefore the possibility of the schema and the defaults disagreeing.
 */
function coerce(value: unknown, template: unknown, path: string): unknown {
  if (Array.isArray(template)) {
    const items =
      typeof value === 'string'
        ? value
            .split(',')
            .map((part) => part.trim())
            .filter((part) => part.length > 0)
        : value;
    if (!Array.isArray(items)) {
      throw new ConfigError(`${path}: expected a list, got ${describe(value)}`);
    }
    return items.map((item) => (typeof item === 'string' ? item : String(item)));
  }

  if (typeof template === 'boolean') {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const normalised = value.trim().toLowerCase();
      if (['true', 'yes', '1', 'on'].includes(normalised)) return true;
      if (['false', 'no', '0', 'off'].includes(normalised)) return false;
    }
    throw new ConfigError(`${path}: expected a boolean, got ${describe(value)}`);
  }

  if (typeof template === 'number') {
    // Booleans must not slip through as 0/1: `true` is a typo here, not a quantity.
    if (typeof value === 'boolean') {
      throw new ConfigError(`${path}: expected a number, got boolean ${String(value)}`);
    }
    const parsed = typeof value === 'string' ? Number(value.trim()) : value;
    if (typeof parsed !== 'number' || !Number.isFinite(parsed)) {
      throw new ConfigError(`${path}: expected a finite number, got ${describe(value)}`);
    }
    return parsed;
  }

  if (typeof template === 'string') {
    if (typeof value === 'string') return value;
    throw new ConfigError(`${path}: expected a string, got ${describe(value)}`);
  }

  throw new ConfigError(`${path}: unsupported policy value type`);
}

/** Translate `FOCUS_AGENT__SECTION__KEY` variables into a nested object. */
export function envToNested(env: Record<string, string | undefined>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [rawKey, rawValue] of Object.entries(env)) {
    if (rawValue === undefined || !rawKey.startsWith(ENV_PREFIX)) continue;
    const parts = rawKey
      .slice(ENV_PREFIX.length)
      .split('__')
      .filter((part) => part.length > 0)
      .map((part) => snakeToCamel(part.toLowerCase()));
    if (parts.length < 2) {
      throw new ConfigError(
        `${rawKey}: environment overrides need a section and a key, e.g. ${ENV_PREFIX}CAMERA__WIDTH=1280`,
      );
    }
    let cursor = result;
    for (const part of parts.slice(0, -1)) {
      const next = cursor[part];
      if (next === undefined) {
        const created: Record<string, unknown> = {};
        cursor[part] = created;
        cursor = created;
      } else if (isPlainObject(next)) {
        cursor = next as Record<string, unknown>;
      } else {
        throw new ConfigError(`${rawKey}: conflicts with another override`);
      }
    }
    cursor[parts[parts.length - 1] as string] = rawValue;
  }
  return result;
}

/** Accept both nested objects and flat `"section.key"` keys (CLI/query-string style). */
function explodeDotted(input: Record<string, unknown> | object): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    const parts = key.split('.');
    let cursor = result;
    for (const part of parts.slice(0, -1)) {
      const next = cursor[part];
      if (isPlainObject(next)) {
        cursor = next as Record<string, unknown>;
      } else {
        const created: Record<string, unknown> = {};
        cursor[part] = created;
        cursor = created;
      }
    }
    const leaf = parts[parts.length - 1] as string;
    cursor[leaf] = isPlainObject(value) ? explodeDotted(value as Record<string, unknown>) : value;
  }
  return result;
}

// ---------------------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------------------

/** Return a list of policy problems. An empty list means the policy is coherent. */
export function validatePolicy(policy: Policy): string[] {
  const problems: string[] = [];
  const { thresholds: t, events: e, smoothing: s, vision: v, calibration: c, quality: q, privacy: p } =
    policy;

  // Angle bands ------------------------------------------------------------------
  if (t.yawForwardMax <= 0) problems.push('thresholds.yawForwardMax must be > 0');
  if (t.yawStrongAway <= t.yawForwardMax) {
    problems.push(
      `thresholds.yawStrongAway must exceed thresholds.yawForwardMax (${t.yawStrongAway} <= ${t.yawForwardMax})`,
    );
  }
  if (t.yawStrongAway > 90) problems.push('thresholds.yawStrongAway above 90 is not measurable');
  if (t.pitchUpMax <= 0) problems.push('thresholds.pitchUpMax must be > 0');
  if (t.pitchDownMax <= 0) problems.push('thresholds.pitchDownMax must be > 0');
  if (t.pitchStrongAway <= Math.max(t.pitchUpMax, t.pitchDownMax)) {
    problems.push(
      'thresholds.pitchStrongAway must exceed both thresholds.pitchUpMax and thresholds.pitchDownMax',
    );
  }
  if (!(t.hysteresisDegrees >= 0 && t.hysteresisDegrees < t.yawForwardMax)) {
    problems.push(
      'thresholds.hysteresisDegrees must be >= 0 and < thresholds.yawForwardMax, otherwise ' +
        'returning to FORWARD becomes impossible',
    );
  }
  if (t.rollTolerance <= 0) problems.push('thresholds.rollTolerance must be > 0');

  // Event duration bands ---------------------------------------------------------
  if (e.ignoreBelowSeconds < 0) problems.push('events.ignoreBelowSeconds must be >= 0');
  if (e.attentionAwayMinSeconds < e.ignoreBelowSeconds) {
    problems.push('events.attentionAwayMinSeconds must be >= events.ignoreBelowSeconds');
  }
  if (e.potentiallySignificantSeconds <= e.attentionAwayMinSeconds) {
    problems.push(
      'events.potentiallySignificantSeconds must exceed events.attentionAwayMinSeconds',
    );
  }
  if (e.faceLostAwaySeconds <= 0) problems.push('events.faceLostAwaySeconds must be > 0');
  if (e.idleTimeoutSeconds <= 0) problems.push('events.idleTimeoutSeconds must be > 0');
  if (e.minStateDwellSeconds < 0) problems.push('events.minStateDwellSeconds must be >= 0');
  if (e.maxEventsPerMinute < 1) problems.push('events.maxEventsPerMinute must be >= 1');

  // Smoothing --------------------------------------------------------------------
  if (s.windowSeconds <= 0) problems.push('smoothing.windowSeconds must be > 0');
  if (s.minSamples < 1) problems.push('smoothing.minSamples must be >= 1');
  if (s.maxSampleAgeSeconds < s.windowSeconds) {
    problems.push('smoothing.maxSampleAgeSeconds must be >= smoothing.windowSeconds');
  }
  if (s.stabilityReferenceDegrees <= 0) {
    problems.push('smoothing.stabilityReferenceDegrees must be > 0');
  }
  const requiredFps = s.minSamples / s.windowSeconds;
  if (v.minProcessFps < requiredFps) {
    problems.push(
      `vision.minProcessFps (${v.minProcessFps}) cannot supply smoothing.minSamples ` +
        `(${s.minSamples}) within smoothing.windowSeconds (${s.windowSeconds}); needs >= ` +
        `${requiredFps.toFixed(2)} fps or the agent will report UNKNOWN under load`,
    );
  }

  // Vision -----------------------------------------------------------------------
  for (const key of [
    'minDetectionConfidence',
    'minLandmarkConfidence',
    'minPoseConfidence',
    'presenceConfidence',
  ] as const) {
    const value = v[key];
    if (!(value >= 0 && value <= 1)) problems.push(`vision.${key} must be within [0, 1]`);
  }
  if (!(v.minProcessFps > 0 && v.minProcessFps <= v.targetProcessFps && v.targetProcessFps <= v.maxProcessFps)) {
    problems.push(
      'vision fps values must satisfy 0 < minProcessFps <= targetProcessFps <= maxProcessFps',
    );
  }
  if (v.maxFaces < 1) problems.push('vision.maxFaces must be >= 1');
  if (v.maxFaces < 2) {
    problems.push(
      'vision.maxFaces must be >= 2 so that "more than one person in frame" is observable; ' +
        'with a single-face detector a bystander cannot be distinguished from the employee ' +
        'and their head movements would be attributed to them',
    );
  }
  if (v.frameBudgetMs <= 0) problems.push('vision.frameBudgetMs must be > 0');
  if (v.maxReprojectionErrorPx <= 0) problems.push('vision.maxReprojectionErrorPx must be > 0');
  if (v.inferenceWidth < 160) {
    problems.push('vision.inferenceWidth below 160 px does not support reliable landmarks');
  }
  if (!['auto', 'cpu', 'gpu'].includes(v.delegate)) {
    problems.push("vision.delegate must be one of 'auto', 'cpu', 'gpu'");
  }
  if (/^https?:\/\//i.test(v.modelAssetPath) || /^https?:\/\//i.test(v.wasmBasePath)) {
    problems.push(
      'vision.modelAssetPath and vision.wasmBasePath must be local paths, not remote URLs. ' +
        'Fetching the model or WASM runtime from a third-party host at runtime breaks ' +
        'offline and air-gapped operation and adds a supply-chain dependency to a ' +
        'monitoring agent. Vendor the assets with `npm run fetch:models`.',
    );
  }

  // Quality ----------------------------------------------------------------------
  if (q.minBrightness >= q.maxBrightness) {
    problems.push('quality.minBrightness must be < quality.maxBrightness');
  }
  if (!(q.minFaceBoxRatio > 0 && q.minFaceBoxRatio < 1)) {
    problems.push('quality.minFaceBoxRatio must be within (0, 1)');
  }
  if (q.minBlurVariance < 0) problems.push('quality.minBlurVariance must be >= 0');

  // Calibration ------------------------------------------------------------------
  if (!(c.thresholdFraction > 0 && c.thresholdFraction < 1)) {
    problems.push('calibration.thresholdFraction must be within (0, 1)');
  }
  if (c.minYawThreshold >= c.maxYawThreshold) {
    problems.push('calibration.minYawThreshold must be < calibration.maxYawThreshold');
  }
  if (c.minPitchThreshold >= c.maxPitchThreshold) {
    problems.push('calibration.minPitchThreshold must be < calibration.maxPitchThreshold');
  }
  if (c.samplesPerStep < 3) {
    problems.push('calibration.samplesPerStep must be >= 3 for a stable median');
  }
  if (c.secondsPerStep <= c.settleSeconds) {
    problems.push('calibration.secondsPerStep must exceed calibration.settleSeconds');
  }
  if (c.maxProfileAgeDays < 1) problems.push('calibration.maxProfileAgeDays must be >= 1');

  // Privacy ----------------------------------------------------------------------
  if (p.retentionDays < 1) problems.push('privacy.retentionDays must be >= 1');
  if (p.uploadBatchSeconds < 1) problems.push('privacy.uploadBatchSeconds must be >= 1');
  if (p.storeRawFrames) {
    problems.push(
      'privacy.storeRawFrames is enabled. Raw frame capture is disabled by default (§28) and ' +
        'requires an authorised, time-boxed approval; set ' +
        `${RAW_FRAME_ACK_ENV}=1 to acknowledge the audited override`,
    );
  }
  if (p.collectSignals.length === 0) {
    problems.push('privacy.collectSignals must list at least one signal family');
  }
  const unknownSignals = p.collectSignals.filter((family) => !KNOWN_SIGNAL_FAMILIES.has(family));
  if (unknownSignals.length > 0) {
    problems.push(
      `privacy.collectSignals contains unknown families: ${unknownSignals.sort().join(', ')} ` +
        `(known: ${[...KNOWN_SIGNAL_FAMILIES].sort().join(', ')})`,
    );
  }
  if (p.collectSignals.includes('head_pose') && !p.collectSignals.includes('face_detected')) {
    problems.push("privacy.collectSignals: 'head_pose' requires 'face_detected'");
  }
  if (p.requireVisibleIndicator && !p.indicatorProvidedByHost && !policy.presentation.showDebugPanel) {
    // The indicator itself is the host's responsibility; this only catches the case where
    // nothing at all would be shown.
    problems.push(
      'privacy.requireVisibleIndicator is true but no indicator is declared: monitoring must ' +
        'always be visible to the employee (§25). Set privacy.indicatorProvidedByHost=true ' +
        'only if the host application shows its own persistent indicator.',
    );
  }

  // Enrolment --------------------------------------------------------------------
  const en = policy.enrolment;
  if (en.challengeCount < 1) problems.push('enrolment.challengeCount must be >= 1');
  if (en.challengeCount > 5) {
    problems.push('enrolment.challengeCount cannot exceed the number of available challenges (5)');
  }
  if (en.holdSeconds <= 0) problems.push('enrolment.holdSeconds must be > 0');
  if (en.challengeTimeoutSeconds <= en.holdSeconds) {
    problems.push(
      'enrolment.challengeTimeoutSeconds must exceed enrolment.holdSeconds, or no challenge ' +
        'can ever be completed',
    );
  }
  if (en.sessionTimeoutSeconds <= en.challengeTimeoutSeconds) {
    problems.push(
      'enrolment.sessionTimeoutSeconds must exceed enrolment.challengeTimeoutSeconds',
    );
  }
  if (en.minChallengeYawDegrees <= 0) problems.push('enrolment.minChallengeYawDegrees must be > 0');
  if (en.minChallengeYawDegrees >= t.yawStrongAway) {
    problems.push(
      'enrolment.minChallengeYawDegrees must stay below thresholds.yawStrongAway, or the ' +
        'required turn is beyond the range the pose solver reports reliably',
    );
  }
  if (en.minChallengePitchDegrees <= 0) {
    problems.push('enrolment.minChallengePitchDegrees must be > 0');
  }
  if (!(en.smileThreshold > 0 && en.smileThreshold <= 1)) {
    problems.push('enrolment.smileThreshold must be within (0, 1]');
  }
  if (!(en.minFrameConfidence >= 0 && en.minFrameConfidence <= 1)) {
    problems.push('enrolment.minFrameConfidence must be within [0, 1]');
  }

  // Session guard ----------------------------------------------------------------
  const g = policy.sessionGuard;
  if (!['log_only', 'enforce'].includes(g.mode)) {
    problems.push("sessionGuard.mode must be 'log_only' or 'enforce'");
  }
  if (g.faceAbsentLockSeconds <= 0) problems.push('sessionGuard.faceAbsentLockSeconds must be > 0');
  if (g.identityMismatchLockSeconds <= 0) {
    problems.push('sessionGuard.identityMismatchLockSeconds must be > 0');
  }
  if (!(g.minMatchSimilarity > 0 && g.minMatchSimilarity < 1)) {
    problems.push('sessionGuard.minMatchSimilarity must be within (0, 1)');
  }
  if (g.identityResultMaxAgeSeconds <= g.identityCheckIntervalSeconds) {
    problems.push(
      'sessionGuard.identityResultMaxAgeSeconds must exceed identityCheckIntervalSeconds, or ' +
        'every result is stale before the next check runs',
    );
  }
  if (g.graceSecondsAfterUnlock < 0) {
    problems.push('sessionGuard.graceSecondsAfterUnlock must be >= 0');
  }
  if (!['face', 'password', 'either'].includes(g.reauthMethod)) {
    problems.push("sessionGuard.reauthMethod must be 'face', 'password' or 'either'");
  }
  if (g.enabled && g.mode === 'enforce' && !policy.enrolment.required) {
    problems.push(
      'sessionGuard is set to enforce with identity checks, but enrolment.required is false: ' +
        'un-enrolled operators have no template to match against and would be locked out on a ' +
        'condition they cannot satisfy',
    );
  }

  if (!['debug', 'info', 'warn', 'error'].includes(policy.runtime.logLevel)) {
    problems.push("runtime.logLevel must be one of 'debug', 'info', 'warn', 'error'");
  }
  if (policy.runtime.maxRunSeconds < 0) problems.push('runtime.maxRunSeconds must be >= 0');
  if (policy.runtime.heartbeatSeconds <= 0) problems.push('runtime.heartbeatSeconds must be > 0');

  return problems;
}

export function requireValidPolicy(policy: Policy): Policy {
  const problems = validatePolicy(policy);
  if (problems.length > 0) throw new ConfigError('Invalid work-focus policy', problems);
  return policy;
}

function isAcknowledgedRawFrameProblem(
  problem: string,
  env: Record<string, string | undefined> | undefined,
): boolean {
  if (!problem.includes('privacy.storeRawFrames')) return false;
  const value = (env?.[RAW_FRAME_ACK_ENV] ?? '').trim().toLowerCase();
  return ['1', 'true', 'yes', 'on'].includes(value);
}

// ---------------------------------------------------------------------------------------
// Identity and inspection
// ---------------------------------------------------------------------------------------

/**
 * Stable short fingerprint of the resolved policy, stamped onto every emitted event.
 *
 * FNV-1a over canonical JSON. Not a cryptographic digest and not meant to be — its job is
 * to let a metric be traced back to the exact thresholds that produced it, so that a score
 * can still be explained months later.
 */
export function policyVersion(policy: Policy): string {
  const canonical = JSON.stringify(sortKeys(policy as unknown as Record<string, unknown>));
  let hash = 0xcbf29ce484222325n;
  const prime = 0x100000001b3n;
  const mask = 0xffffffffffffffffn;
  for (let index = 0; index < canonical.length; index += 1) {
    hash = (hash ^ BigInt(canonical.charCodeAt(index))) & mask;
    hash = (hash * prime) & mask;
  }
  return hash.toString(16).padStart(16, '0').slice(0, 12);
}

/** Yield `["section.key", value]` for every leaf — used by policy inspection screens. */
export function flattenPolicy(policy: Policy): Array<[string, unknown]> {
  const output: Array<[string, unknown]> = [];
  const walk = (node: Record<string, unknown>, prefix: string): void => {
    for (const key of Object.keys(node).sort()) {
      const value = node[key];
      const dotted = prefix ? `${prefix}.${key}` : key;
      if (isPlainObject(value)) {
        walk(value as Record<string, unknown>, dotted);
      } else {
        output.push([dotted, value]);
      }
    }
  };
  walk(policy as unknown as Record<string, unknown>, '');
  return output;
}

export function provenanceReport(resolved: ResolvedPolicy): string {
  const entries = Object.entries(resolved.sources);
  if (entries.length === 0) return 'All values are built-in defaults.';
  const width = Math.max(...entries.map(([key]) => key.length));
  return entries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, source]) => `${key.padEnd(width)}  <- ${source}`)
    .join('\n');
}

// ---------------------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------------------

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function describe(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'a list';
  return typeof value;
}

function snakeToCamel(value: string): string {
  return value.replace(/_([a-z0-9])/g, (_match, char: string) => char.toUpperCase());
}

function sortKeys(value: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(value).sort()) {
    const child = value[key];
    result[key] = isPlainObject(child) ? sortKeys(child as Record<string, unknown>) : child;
  }
  return result;
}

function structuredCloneCompat<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
